import { Button, MenuProps, Spin, Switch } from 'antd';
import SearchBox from 'components/molecules/SearchBox';
import { UserContext } from 'contexts/ConfigContext';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { queryClient } from 'services/reactQueryService';
import { getAllPlansVariablesType } from 'types/tradePromoOptimization';
import {
    compareUsers,
    downloadBlob,
    extractErrorDetailFromResponse,
    generateErrorMsg,
    getConfig,
    sendNotification,
    transformFiltersV2,
    transformFiltersV3,
} from 'utils/helperMethods';
import { LoadingOutlined, PlusSquareOutlined } from '@ant-design/icons';
import PlansIndexTableWrapper from 'components/template/TradePromoOptimization/PlansIndexTableWrapper';
import CreateEditPlanContainer from 'components/molecules/TradePromoOptimization/Modals/CreateEditPlanContainer';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import {
    ErrorMessages,
    TPO_APPROVAL_PUBLISH_FEATURE_NAME,
    TPO_APPROVAL_PUBLISH_TOGGLE_QUERY_KEY,
    tpoNotificationMessages,
    tpoReactQueryKeyConstants,
} from 'utils/Enums';
import { TPODataContext, initialPlanEditMetaData } from 'contexts/TPOContext';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import { DimensionDropdownType } from 'types/pricingSimulator';
import _ from 'lodash';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { CustomDropdownButton } from 'components/molecules/PricingSimulator/ScenarioActions';

const subHeader = '';

export type CreatePlanModalType = {
    open: boolean;
    isEdit: boolean;
    data: Record<string, any>;
};

export type DeletePlanModalType = {
    open: boolean;
    data: Record<string, any>;
};

const initialModalState = { open: false, data: {}, isEdit: false };
const initialDeleteModalState: DeletePlanModalType = { open: false, data: {} };
const antIconDanger = <LoadingOutlined style={{ fontSize: 18, color: 'red' }} spin />;

//TPO Plans index page
const Plans = () => {
    const {
        rbacConditions,
        rbacLoading,
        showSyncedPlans,
        setShowSyncedPlans,
        selectedPlanInfo,
        setPlanEditMetaData,
        approvalPublishEnabled,
        approvalPublishToggleLoading,
    } = useContext(TPODataContext);

    const { appConfig } = useContext(UserContext);
    const tradePromoOptimization = getConfig('tradePromoOptimization', appConfig);
    const enableEditMode = tradePromoOptimization?.enableEditMode;
    const { planIndexFilterKeys, filterDimensionInfo } = tradePromoOptimization;
    const limit = tradePromoOptimization?.indexPageTableLimit;
    const [searchKey, setSearchKey] = useState<string>('');

    const [tableReactQueryVariables, setTableReactQueryVariables] = useState<getAllPlansVariablesType>({
        search_keyword: '',
        limit,
        offset: 0,
        sort_by: 'updated_at',
        sorting_order: 'desc',
        user_id: localStorage.getItem('email'),
        plan_type: null,
        show_synced_plans: showSyncedPlans,
        filters: {},
    });
    const [createEditPlanModal, setCreateEditPlanModal] = useState<CreatePlanModalType>({ ...initialModalState });
    const [deletePlanModal, setDeletePlanModal] = useState<DeletePlanModalType>({ ...initialDeleteModalState });
    const [openToggleApprovalPublishModal, setOpenToggleApprovalPublishModal] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});
    // Use TPOContext filter state for persistence
    const {
        plansIndexAllCheckedAttribute,
        setPlansIndexAllCheckedAttribute,
        plansIndexAllSelectedValueForAllAttribute,
        setPlansIndexAllSelectedValueForAllAttribute,
        plansIndexApplyFilterObject,
        setPlansIndexApplyFilterObject,
        updatePlansFilterObjectWithToolsSharing,
    } = useContext(TPODataContext);

    const [availableFilters, setAvailableFilters] = useState<DimensionDropdownType[]>([]);

    //release plan
    const { mutate: releasePlanEditFn, loading: releasePlanEditLoading } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${createEditPlanModal?.data?.plan_id}/edit/release`,
        method: 'post',
    });
    //acquire plan
    const { isPending: editPlanAcquireLoading, mutate: editPlanAcquireFn } = useMutation({
        mutationFn: async (inputData: any) => {
            //write onsuccess and onerror where mutate function is called
            try {
                const data = await axios({
                    method: 'post',
                    url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${inputData?.planId}/edit/acquire`,
                    data: { user_id: localStorage.getItem('email') },
                });
                return data;
            } catch (error) {
                throw error;
            }
        },
        onSuccess: (data) => {},
        onError: (error: any) => {},
    });
    //acquire plan mutation
    function acquireEditPlan(planId: number) {
        if (editPlanAcquireLoading) return;
        editPlanAcquireFn(
            { planId },
            {
                onSuccess: (data) => {
                    //if user is same, then it means that edit rights are acquired
                    if (compareUsers(data?.data?.edit_lock?.editing_user)) {
                        sendNotification('success', tpoNotificationMessages.PLAN_EDIT_ACQUIRE_SUCCESS);
                    } else {
                        sendNotification(
                            'error',
                            `User ${data?.data?.edit_lock?.editing_user} is currently editing this plan`,
                        );
                    }
                    setPlanEditMetaData({
                        editingUser: data?.data?.edit_lock?.editing_user,
                        sessionExpiresInSecs: data?.data?.edit_lock?.session_expires_in_sec,
                        startedAt: data?.data?.edit_lock?.started_at,
                        planId: data?.data?.plan_id,
                    });
                },
                onError: (error: any) => {
                    const errorMsg = generateErrorMsg(
                        String(error?.response?.data?.detail || ''),
                        ErrorMessages.GENERALIZED_ERROR_MSG,
                    );
                    //show error message
                    sendNotification('error', errorMsg);
                },
            },
        );
    }

    const { loading: deletePlanLoading, mutate: deletePlanFn } = useCommonMutationHandler({
        url: `${tradePromoOptimizationUrls.deletePlanUrl}/${deletePlanModal.data?.plan_id}`,
        method: 'delete',
    });

    // Plans-index export. Backend returns a ready-to-download .xlsx directly,
    // so we pass `responseType: 'blob'` to keep the binary payload intact.
    const { loading: exportPlansLoading, mutate: exportPlansFn } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.getExportPlansUrl,
        method: 'post',
        responseType: 'blob',
    });

    const { loading: toggleApprovalPublishLoading, mutate: toggleApprovalPublishFn } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.featureToggleUrl,
        method: 'post',
    });

    function handleDeletePlan() {
        deletePlanFn(
            {},
            {
                onSuccess: () => {
                    sendNotification('success', 'Plan deleted successfully');
                    setDeletePlanModal({ ...initialDeleteModalState });
                    queryClient.refetchQueries({
                        exact: true,
                        queryKey: [tpoReactQueryKeyConstants.GET_ALL_PLANS, tableReactQueryVariables],
                    });
                },
                onError: (error: any) => {
                    const errorMsg = generateErrorMsg(
                        String(error?.response?.data?.detail || ''),
                        ErrorMessages.GENERALIZED_ERROR_MSG,
                    );
                    sendNotification('error', errorMsg);
                },
            },
        );
    }

    function releaseEditRights() {
        if (releasePlanEditLoading) return;
        const payload = {
            user_id: localStorage.getItem('email'),
        };
        releasePlanEditFn(payload, {
            onSuccess: (data) => {
                setPlanEditMetaData({
                    ...initialPlanEditMetaData,
                });
            },
            onError: (error: any) => {
                setPlanEditMetaData({
                    ...initialPlanEditMetaData,
                });
            },
        });
    }
    //called when clicked on cancel
    function resetModalStates() {
        if (createEditPlanModal?.isEdit && enableEditMode) releaseEditRights();
        setCreateEditPlanModal({ ...initialModalState, data: createEditPlanModal?.data });
    }

    //called when plan is created
    function onCreatePlanFinish(planId: number) {
        //acquire plan when create plan is done
        if (!createEditPlanModal?.isEdit) {
            acquireEditPlan(planId);
        }

        sendNotification(
            'success',
            createEditPlanModal?.isEdit
                ? tpoNotificationMessages.EDIT_PLAN_SUCCESS
                : tpoNotificationMessages.CREATE_PLAN_SUCCESS,
        );

        resetModalStates();
        queryClient.refetchQueries({
            exact: true,
            queryKey: [tpoReactQueryKeyConstants.GET_ALL_PLANS, tableReactQueryVariables],
        });
    }

    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        setTableReactQueryVariables({ ...tableReactQueryVariables, offset: 0, search_keyword: val });
    };

    const resetFilterObject = () => {
        const allSelectedObj: any = {};
        const selectAllObj: any = {};
        const newApplyFilterObj: any = {};
        planIndexFilterKeys.forEach((filter: any) => {
            allSelectedObj[filter.value] = new Set();
            newApplyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(planIndexFilterKeys);

        // Reset Plans-specific states
        setPlansIndexAllCheckedAttribute(allSelectedObj);
        setPlansIndexApplyFilterObject(_.cloneDeep(newApplyFilterObj));
        setPlansIndexAllSelectedValueForAllAttribute(selectAllObj);

        // Immediately update table variables with empty filters
        const updatedFilters = transformFiltersV2(newApplyFilterObj, planIndexFilterKeys);
        setTableReactQueryVariables((prev) => ({
            ...prev,
            filters: updatedFilters,
            offset: 0,
        }));
    };

    // Initialize filters using Plans-specific TPOContext
    useEffect(() => {
        // Use Plans-specific filter management to avoid interference from other tabs
        updatePlansFilterObjectWithToolsSharing(planIndexFilterKeys);
        setAvailableFilters(planIndexFilterKeys);
    }, [planIndexFilterKeys]);

    const onFilterSaveClickHandler = (resultObj: any) => {
        setPlansIndexAllCheckedAttribute(resultObj.allCheckedAttribute);
        setPlansIndexAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setPlansIndexApplyFilterObject(resultObj.applyFilterObject);
    };

    // Update table variables when filters change
    useEffect(() => {
        const updatedFilters = transformFiltersV2(plansIndexApplyFilterObject, planIndexFilterKeys);
        setTableReactQueryVariables((prev) => ({ ...prev, filters: updatedFilters, offset: 0 }));
    }, [plansIndexApplyFilterObject]);

    useEffect(() => {
        setTableReactQueryVariables({
            ...tableReactQueryVariables,
            offset: 0,
            show_synced_plans: showSyncedPlans,
        });
    }, [showSyncedPlans]);

    const commonVariables = useMemo(() => {
        if (selectedFilter?.value) {
            // const updatedFilters = transformFiltersV3(tempApplyFilterObject, planIndexFilterKeys, selectedFilter.value);
            const updatedFilters = transformFiltersV2(tempApplyFilterObject, planIndexFilterKeys, selectedFilter.value);
            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
                show_synced_plans: showSyncedPlans,
            };
        }
    }, [selectedFilter, showSyncedPlans]);

    const handleExportPlans = () => {
        if (exportPlansLoading) return;
        const payload = {
            // Hardcoded to always request the entire dataset — the export
            // deliberately ignores the user's currently applied filters.
            filters: {
                dim1: { inclusion: [], exclusion: [] },
                dim2: { inclusion: [], exclusion: [] },
                dim10: { inclusion: [], exclusion: [] },
            },
            search_keyword: '',
            sort_by: 'updated_at',
            sorting_order: 'desc',
        };
        sendNotification('info', 'Export in progress. Your download will start shortly.');
        exportPlansFn(payload, {
            onSuccess: (data) => {
                downloadBlob(data?.data as Blob, 'plans_export.xlsx');
                sendNotification('success', 'Export is ready! The download has started automatically', 5, 500);
            },
            onError: async (error: any) => {
                const backendDetail = await extractErrorDetailFromResponse(error);
                const errorMsg = generateErrorMsg(backendDetail, ErrorMessages.GENERALIZED_ERROR_MSG);
                sendNotification('error', errorMsg);
            },
        });
    };

    const handleToggleApprovalPublish = () => {
        if (toggleApprovalPublishLoading) return;
        const nextState = !approvalPublishEnabled;
        toggleApprovalPublishFn(
            { feature_name: TPO_APPROVAL_PUBLISH_FEATURE_NAME, enable_feature: nextState },
            {
                onSuccess: () => {
                    setOpenToggleApprovalPublishModal(false);
                    sendNotification('success', `Plan approval & publish ${nextState ? 'enabled' : 'disabled'}`);
                    queryClient.refetchQueries({
                        exact: true,
                        queryKey: [TPO_APPROVAL_PUBLISH_TOGGLE_QUERY_KEY],
                    });
                },
                onError: async (error: any) => {
                    const backendDetail = await extractErrorDetailFromResponse(error);
                    const errorMsg = generateErrorMsg(backendDetail, ErrorMessages.GENERALIZED_ERROR_MSG);
                    sendNotification('error', errorMsg);
                },
            },
        );
    };

    const plansIndexDropdownItems: MenuProps['items'] = useMemo(
        () => [
            {
                disabled: exportPlansLoading,
                label: (
                    <div className="font-xs cursor-pointer">{exportPlansLoading ? 'Exporting…' : 'Export Plans'}</div>
                ),
                key: 'export_plans',
                onClick: handleExportPlans,
            },
            ...(showSyncedPlans && rbacConditions?.showApprovalPublishControl
                ? [
                      {
                          disabled: toggleApprovalPublishLoading || approvalPublishToggleLoading,
                          label: (
                              <div className="font-xs cursor-pointer">
                                  {approvalPublishEnabled ? 'Disable' : 'Enable'} Approval & Publish
                              </div>
                          ),
                          key: 'toggle_approval_publish',
                          onClick: () => setOpenToggleApprovalPublishModal(true),
                      },
                  ]
                : []),
        ],
        // Add state/context values that new items depend on here (mirroring
        // PlanActions.tsx `optionDropdownItems`). Handler references declared
        // inside the component are intentionally excluded — they close over
        // fresh state on every render.
        [
            exportPlansLoading,
            rbacConditions,
            showSyncedPlans,
            approvalPublishEnabled,
            approvalPublishToggleLoading,
            toggleApprovalPublishLoading,
        ],
    );

    return (
        <>
            <div className="flex absolute right-4 top-4 justify-between items-end">
                <div>
                    <h5 className={`font-xs m-0 font-normal text-fontGreyAlt `}>{subHeader} </h5>
                </div>

                <div className="flex items-center gap-2">
                    <SearchBox onSearch={onSearch} search={searchKey} setSearch={setSearchKey} />
                    <div className="flex items-center gap-2">
                        <p className="text-promoColour font-xxs">Operational Plans</p>
                        <Switch
                            onChange={(val) => {
                                setShowSyncedPlans(val);
                            }}
                            checked={showSyncedPlans}
                        />
                    </div>
                    <NewGlobalFilter
                        availableFilters={availableFilters}
                        allCheckedAttribute={plansIndexAllCheckedAttribute}
                        allSelectedValueForAllAttribute={plansIndexAllSelectedValueForAllAttribute}
                        applyFilterObject={plansIndexApplyFilterObject}
                        resetFilterObject={resetFilterObject}
                        onSubmitHandler={onFilterSaveClickHandler}
                        url={tradePromoOptimizationUrls.getPlanIndexFilterAttributes}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={commonVariables}
                        filterDimensionInfo={filterDimensionInfo}
                        showAttributePanelToLeft
                    />
                    <Button
                        disabled={!rbacConditions?.showCreatePlanButton || rbacLoading}
                        style={{ borderRadius: 4 }}
                        onClick={() => {
                            setCreateEditPlanModal({ open: true, data: {}, isEdit: false });
                        }}
                        type="primary"
                        icon={<PlusSquareOutlined />}
                    >
                        Create Plan
                    </Button>
                    <CustomDropdownButton
                        style={{ width: 'auto' }}
                        trigger={['click']}
                        menu={{
                            style: { borderRadius: 8, padding: 4, width: 'auto' },
                            items: plansIndexDropdownItems,
                        }}
                    ></CustomDropdownButton>
                </div>
            </div>
            <div className="my-3">
                <FilterRibbon
                    allCheckedAttribute={plansIndexAllCheckedAttribute}
                    allSelectedValueForAllAttribute={plansIndexAllSelectedValueForAllAttribute}
                    applyFilterObject={plansIndexApplyFilterObject}
                    resetFilterObject={resetFilterObject}
                    onSubmitHandler={(obj: any) => {
                        setPlansIndexAllCheckedAttribute(obj.allCheckedAttribute);
                        setPlansIndexAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                        setPlansIndexApplyFilterObject(obj.applyFilterObject);
                    }}
                />
            </div>
            <div className="my-2 border-b border-borderGreyAlt"></div>
            <PlansIndexTableWrapper
                setCreateEditPlanModal={setCreateEditPlanModal}
                setDeletePlanModal={setDeletePlanModal}
                setTableReactQueryVariables={setTableReactQueryVariables}
                tableReactQueryVariables={tableReactQueryVariables}
            />
            <CreateEditPlanContainer
                onSubmitFinish={onCreatePlanFinish}
                open={createEditPlanModal.open}
                isEdit={createEditPlanModal?.isEdit}
                data={createEditPlanModal?.data}
                onCancel={(e) => {
                    resetModalStates();
                }}
            />
            <ConfirmationModal
                open={deletePlanModal.open}
                onCancel={() => setDeletePlanModal({ ...initialDeleteModalState })}
                onSubmitClick={handleDeletePlan}
                loading={deletePlanLoading}
                okText={deletePlanLoading ? <Spin indicator={antIconDanger} /> : 'Delete'}
                okButtonProps={{ type: 'ghost', danger: true, style: { borderRadius: 6 } }}
                title="Delete Plan"
                subTitle={`Are you sure you want to delete "${deletePlanModal.data?.plan_name}"?`}
                description="Plans once deleted cannot be recovered."
            />
            <ConfirmationModal
                open={openToggleApprovalPublishModal}
                onCancel={() => setOpenToggleApprovalPublishModal(false)}
                onSubmitClick={handleToggleApprovalPublish}
                loading={toggleApprovalPublishLoading}
                okText={
                    toggleApprovalPublishLoading ? (
                        <Spin indicator={approvalPublishEnabled ? antIconDanger : <LoadingOutlined spin />} />
                    ) : approvalPublishEnabled ? (
                        'Disable'
                    ) : (
                        'Enable'
                    )
                }
                okButtonProps={{
                    type: approvalPublishEnabled ? 'ghost' : 'primary',
                    danger: approvalPublishEnabled,
                    style: { borderRadius: 6 },
                }}
                title={`${approvalPublishEnabled ? 'Disable' : 'Enable'} Approval & Publish`}
                subTitle={`Are you sure you want to ${
                    approvalPublishEnabled ? 'disable' : 'enable'
                } plan approval and publish?`}
                description=""
            />
        </>
    );
};

export default Plans;
